import { DEV } from 'esm-env';
import { wait } from '../async.ts';
import { log } from '../core.ts';
import { isAbsent, isPresent, isString } from '../identity.ts';
import type { LruOptions } from '../lru.ts';
import { Lru } from '../lru.ts';
import type { Scalar, Time } from '../units.ts';
import { Seconds } from '../units.ts';
import type { MaybeAnnotated } from './annotations.ts';
import { createDefault } from './build.ts';
import type { Descriptor, RefId, Struct } from './descriptor.ts';

const devVerbosity = 1 as number; // 1; // 2;
const verbosity = DEV ? devVerbosity : 0;

export type RefDoc<T> = {
  readonly id: string;
  data(): T;
};

export type MaybeRefDoc<T> = {
  readonly id: string;
  data(): T | undefined;
};

export type QueryResult<T> = {
  docs: RefDoc<T>[];
};

// TODO: Configure error or log on cache overflow.
export abstract class DescriptorCache {
  static get i() {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    DescriptorCache.active_ ??= new DummyCache();
    return DescriptorCache.active_;
  }

  static install(cache: DescriptorCache) {
    DescriptorCache.active_ = cache;
  }

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  private static active_: DescriptorCache | undefined;

  constructor(options?: LruOptions) {
    options ??= {
      maxCount: 10000,
      maxAge: Seconds(60),
    };
    this.cache_ = new Lru<
      string,
      Promise<RefDoc<unknown>> | RefDoc<unknown> | Promise<RefDoc<unknown>[]> | RefDoc<unknown>[]
    >(options);
  }

  async lookup<T extends Struct>(
    desc: Descriptor<T>,
    ref: MaybeAnnotated<RefId<T>> | undefined,
  ): Promise<T | undefined> {
    if (isAbsent(ref)) {
      return undefined;
    }
    const result = await this.lookupDoc(desc, ref);
    return result?.data();
  }

  async lookupAllDocs<T extends Struct>(
    desc: Descriptor<T>,
    ref: Array<MaybeAnnotated<RefId<T>> | string>,
  ): Promise<RefDoc<T>[]> {
    const results = await Promise.all(ref.map((r) => this.lookupDoc(desc, r)));
    return results.filter((d) => isPresent(d.data())) as RefDoc<T>[];
  }

  lookupDoc<T extends Struct>(
    desc: Descriptor<T>,
    ref: MaybeAnnotated<RefId<T>> | string,
  ): Promise<MaybeRefDoc<T>> | MaybeRefDoc<T> {
    if (isString(ref)) {
      ref = { id: ref, path: `/${desc.meta.name}/${ref}` };
    }
    let result: Promise<MaybeRefDoc<T>> | MaybeRefDoc<T> | null = this.cache_.get(
      ref.path,
      null,
    ) as Promise<MaybeRefDoc<T>> | MaybeRefDoc<T> | null;
    if (result !== null) {
      if (verbosity > 1) {
        log(
          'Cache.hit',
          {
            path: ref.annotation ?? ref.path,
            v: result instanceof Promise ? '(pending)' : result,
          },
          { verbosity: 'terse' },
        );
      }
    } else {
      if (verbosity > 1) {
        log('Cache.miss', { path: ref.annotation ?? ref.path });
      }
      result = this.doLookup(desc, ref);
      if (this.cache_.set(ref.path, result)) {
        console.warn('Cache.full');
      }
      if (result instanceof Promise) {
        void result.then((v) => {
          if (verbosity > 0) {
            log('Cache.store', { path: ref.annotation ?? ref.path, v }, { verbosity: 'terse' });
          }
          if (this.cache_.set(ref.path, v)) {
            console.warn('Cache.full');
          }
        });
      }
    }
    return result;
  }

  query<T extends Struct>(desc: Descriptor<T>, ref: MaybeAnnotated<RefId<T>>) {
    let result: Promise<RefDoc<T>[]> | RefDoc<T>[] | null = this.cache_.get(ref.path, null) as
      | Promise<RefDoc<T>[]>
      | RefDoc<T>[]
      | null;
    if (result !== null) {
      if (verbosity > 1) {
        log(
          'Cache.hit',
          {
            path: ref.annotation ?? ref.path,
            v: result instanceof Promise ? '(pending)' : result,
          },
          { verbosity: 'terse' },
        );
      }
    } else {
      if (verbosity > 1) {
        log('Cache.query.miss', { path: ref.annotation ?? ref.path });
      }
      result = this.doQuery(desc, ref);
      if (this.cache_.set(ref.path, result)) {
        console.warn('Cache.full');
      }
      if (result instanceof Promise) {
        void result.then((docs) => {
          if (verbosity > 0) {
            log(
              'Cache.query.store',
              { path: ref.annotation ?? ref.path, docs: docs.length },
              { verbosity: 'terse' },
            );
          }
          if (this.cache_.set(ref.path, docs)) {
            console.warn('Cache.full');
          }
        });
      }
    }
    return result;
  }

  protected abstract doLookup<T extends Struct>(
    desc: Descriptor<T>,
    ref: RefId<T>,
  ): Promise<MaybeRefDoc<T>> | MaybeRefDoc<T>;

  protected abstract doQuery<T extends Struct>(
    desc: Descriptor<T>,
    ref: RefId<T>,
  ): Promise<RefDoc<T>[]> | RefDoc<T>[];

  private cache_: Lru<
    string,
    Promise<RefDoc<unknown>> | RefDoc<unknown> | Promise<RefDoc<unknown>[]> | RefDoc<unknown>[]
  >;
}

class DummyCache extends DescriptorCache {
  override doLookup<T extends Struct>(_desc: Descriptor<T>, ref: RefId<T>) {
    return { id: ref.id, data: () => undefined };
  }
  override doQuery<T extends Struct>(_desc: Descriptor<T>, ref: RefId<T>) {
    return [];
  }
}

export class TestCache extends DescriptorCache {
  constructor(readonly delay: Scalar<Time>) {
    super();
  }

  override doLookup<T extends Struct>(desc: Descriptor<T>, ref: RefId<T>) {
    const result = {
      id: ref.id,
      data: () => createDefault(desc),
    };
    if (this.delay.isEqualTo(Seconds(0))) {
      return result;
    }
    return wait(this.delay).then(() => result);
  }

  override doQuery<T extends Struct>(desc: Descriptor<T>, ref: RefId<T>) {
    const result = [
      {
        id: 'id',
        data: () => createDefault(desc),
      },
    ];
    if (this.delay.isEqualTo(Seconds(0))) {
      return result;
    }
    return wait(this.delay).then(() => result);
  }
}
